import Typography from '@mui/material/Typography';
import { useEmailByOobCode, useResetPassword } from 'features/user';
import React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { LoginLogo } from 'shared/components/ui/login-logo';
import { QueryParamOptions, useQueryParam } from 'shared/lib/react-router-dom';
import { Paths } from 'shared/routes/paths';

import { ResetPasswordForm } from './components/reset-password-form';
import * as Styled from './reset-password.style';

const ResetPasswordContent: React.FC = () => {
  const { t } = useTranslation();
  const [code] = useQueryParam(QueryParamOptions.OobCode);
  const email = useEmailByOobCode(code);
  const { resetPassword, isLoading, error } = useResetPassword();

  const submitForm = ({ password }: { password: string }) =>
    !!code && resetPassword({ password, code });

  return (
    <>
      <Typography variant="h1" textAlign="center">
        {t('forms.reset-password.title')}
      </Typography>

      <Styled.Content>
        <Typography variant="body2">
          {t('forms.reset-password.description')}
        </Typography>
        <Typography variant="body2">
          {t('forms.reset-password.warning')}
        </Typography>

        <ResetPasswordForm
          email={email}
          onSubmit={submitForm}
          isLoading={isLoading}
          error={error ?? undefined}
        />
      </Styled.Content>
    </>
  );
};

const InvalidLinkFallback = () => {
  const { t } = useTranslation();

  return (
    <>
      <Typography variant="h1" textAlign="center">
        {t('forms.reset-password.invalid-link.title')}
      </Typography>

      <Styled.Content>
        <Typography variant="body2" textAlign="center">
          {t('forms.reset-password.invalid-link.description')}
        </Typography>
      </Styled.Content>
    </>
  );
};

export const ResetPassword: React.FC = () => {
  const { t } = useTranslation();

  return (
    <Styled.Container>
      <Styled.Body>
        <Styled.BodyContent>
          <LoginLogo />

          <ErrorBoundary
            fallbackRender={({ error }) => {
              if (error.code === 'auth/invalid-action-code') {
                return <InvalidLinkFallback />;
              }

              throw error;
            }}
          >
            <ResetPasswordContent />
          </ErrorBoundary>

          <Styled.Anchor to={Paths.Login}>
            {t('forms.reset-password.back-to-login.label')}
          </Styled.Anchor>
        </Styled.BodyContent>
      </Styled.Body>
    </Styled.Container>
  );
};
